import 'react-sheet-slide/style.css'

import { MapDirectionsRenderer } from '@c/Catalog'
import RoutesWindow from '@c/Catalog/Map/RoutesWindow'
import { createMarkerIcon, ORANGE_CATEGORIES } from '@c/Catalog/Map/utils/createMarkerIcon'
import { GoogleMap, Marker, MarkerClusterer, TransitLayer } from '@react-google-maps/api'
import debounce from 'lodash/debounce'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import ReactGA from 'react-ga4'
import { useTranslation } from 'react-i18next'
import { useLocation, useSearchParams } from 'react-router-dom'

import SentryErrorBoundary from '@/components/SentryErrorBoundary'
import { Coordinates, IMapObject, InterestPoint, Language, MapPoint } from '@/core/interface'
import { Excursion, TExcursionPoint } from '@/core/interface/Excursion'
import { useAppDispatch, useAppSelector } from '@/core/store'
import {
  getExcursionCategories,
  getExcursionPoints,
  getPointsThunk,
} from '@/core/store/catalog.store'
import { setModal, setModalParams } from '@/core/store/ui.store'
import {
  setLocale,
  setLocationManual,
  setMapAutoCentering,
  setMapLoaded,
  setUserLocation,
} from '@/core/store/ui.store'

import { InfoWindow } from './components/InfoWindow'
import {
  circleOptions,
  containerStyle,
  createCenterControl,
  createSpecialOfferControl,
  mapOptions,
} from './mapStyler'

interface IMainMapProps {
  markers: InterestPoint[]
  isExcursionBeingSelected: boolean
  setExcursionBeingSelected: (state: boolean) => void
  //The following params are to support opening routes window on predefined element
  initialExcursionId?: string
}

// Updated ClusteredMarkers component with display name
const ClusteredMarkers = React.memo(({ markers }: { markers: any[] }) => {
  const orangeClusterUrl =
    'data:image/svg+xml;charset=utf-8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="18" fill="#FF8A00" stroke="#FFFFFF" stroke-width="3" />
      </svg>`,
    )

  const greenClusterUrl =
    'data:image/svg+xml;charset=utf-8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="18" fill="#26A69A" stroke="#FFFFFF" stroke-width="3" />
      </svg>`,
    )

  return (
    <MarkerClusterer
      averageCenter
      enableRetinaIcons
      gridSize={60}
      minimumClusterSize={5}
      options={{
        styles: [
          {
            url: orangeClusterUrl,
            width: 40,
            height: 40,
            textColor: '#ffffff',
            textSize: 14,
          },
          {
            url: greenClusterUrl,
            width: 40,
            height: 40,
            textColor: '#ffffff',
            textSize: 14,
          },
        ],
        calculator: (clusterMarkers: google.maps.Marker[], numStyles: number) => {
          const allFood = clusterMarkers.every((m) => {
            const title = (typeof m.getTitle === 'function' ? m.getTitle() : '') || ''
            return ORANGE_CATEGORIES.has(title.toUpperCase())
          })

          return {
            text: String(clusterMarkers.length),
            index: allFood ? 1 : Math.min(2, numStyles),
          }
        },
      }}
    >
      {(clusterer) => (
        <>
          {markers.map((marker) => (
            <Marker
              key={marker.key}
              position={marker.position}
              icon={marker.icon}
              clusterer={clusterer}
              onClick={marker.onClick}
              zIndex={marker.zIndex}
              title={marker.category}
            />
          ))}
        </>
      )}
    </MarkerClusterer>
  )
})

// Set the display name explicitly
ClusteredMarkers.displayName = 'ClusteredMarkers'

const MainMap: React.FC<IMainMapProps> = ({
  markers,
  isExcursionBeingSelected,
  setExcursionBeingSelected,
  initialExcursionId,
}) => {
  const { userLocation, userPosition, locale, locationManual, mapAutoCentering } = useAppSelector(
    (store) => store.uiState,
  )

  const dispatch = useAppDispatch()
  const { i18n } = useTranslation()
  const location = useLocation()
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [markDto, setPointerDataset] = useState<IMapObject | null>(null)

  const [searchParams] = useSearchParams()

  const { excursions, interestPoints, selectedInterestPoint } = useAppSelector(
    (store) => store.catalogStore,
  )
  const [places, setPlaces] = useState<MapPoint[]>([])
  const [selectedExcursion, setSelectedExcursion] = useState<Excursion | null>(null)
  const [currentStep, setCurrentStep] = useState(0)
  const [selectedPoint, setSelectedPoint] = useState('')
  // Function to set the current step and log the event with Google Analytics
  const setCurrentTrackStep = (step: number) => {
    const idString = selectedExcursion && selectedExcursion.id ? `${selectedExcursion.id}` : ''
    if (step > currentStep) {
      ReactGA.event({
        category: 'excursion',
        action: 'WA_ExcursionStep',
        label: `${idString}_${step}`,
      })
    } else {
      ReactGA.event({
        category: 'excursion',
        action: 'WA_ExcursionStepBack',
        label: `${idString}_${step}`,
      })
    }
    setCurrentStep(step) // Set the new step
  }
  const screenWidth = window.screen.width
  // position setter around the positioning
  const [currentZoom, setCurrentZoom] = useState<number>(() => {
    if (location.state?.lat && location.state?.lon) {
      return screenWidth < 800 ? 17 : screenWidth < 1200 ? 18 : 19
    }
    return 15 // Default zoom
  })
  const [updateTrigger, setUpdateTrigger] = useState(0)

  const [customLatLng, setCustomLatLng] = useState<{ latitude: number; longitude: number } | null>(
    () => {
      if (location.state?.lat && location.state?.lon) {
        return { latitude: location.state.lat, longitude: location.state.lon }
      }
      return null // Default latlon
    },
  )

  useEffect(() => {
    if (!customLatLng) return

    setLocationManual(true)
    // Clear the location state to prevent re-running this effect
    location.state = {}
  }, [customLatLng])

  useEffect(() => {
    if (!selectedInterestPoint) return
    const coordinates = selectedInterestPoint.coordinate
    setCurrentZoom(17)
    setTimeout(() => setCustomLatLng(coordinates), 300)

    void handleMarkerClick(selectedInterestPoint)
  }, [selectedInterestPoint])

  //On Samsung browser mainly, prevent zooming of the entire app page
  //when map is pinched
  useEffect(() => {
    const preventDefault = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault()
      }
    }

    document.addEventListener('touchmove', preventDefault, { passive: false })

    return () => {
      document.removeEventListener('touchmove', preventDefault)
    }
  }, [])

  useEffect(() => {
    if (!searchParams.get('interestPoint') || selectedInterestPoint) return
    const interestPointsBySearch = interestPoints.filter(
      (item) => item.id == searchParams.get('interestPoint'),
    )
    if (!interestPointsBySearch.length) return

    const coordinates = interestPointsBySearch[0].coordinate
    setCurrentZoom(17)
    // setLastManualCenter({ lat: coordinates.latitude, lng: coordinates.longitude })
    // setCustomLatLng(coordinates)
    setTimeout(() => setCustomLatLng(coordinates), 300)

    handleMarkerClick(
      interestPoints.filter((item) => item.id == searchParams.get('interestPoint'))[0],
    )
  }, [])

  // Provide default values for mapCenter and userLocation
  const defaultCenter = { lat: 41.89021, lng: 12.4923 } // Center of Rome
  const [lastManualCenter, setLastManualCenter] = useState<google.maps.LatLngLiteral | null>(null)

  // "smart" center - userLocation is changed rarely
  const mapCenter = useMemo(() => {
    if (customLatLng) {
      return {
        lat: customLatLng.latitude,
        lng: customLatLng.longitude,
      }
    }

    if (mapAutoCentering && userLocation) {
      return {
        lat: userLocation.latitude,
        lng: userLocation.longitude,
      }
    }

    if (lastManualCenter) {
      return lastManualCenter
    }

    return defaultCenter
  }, [userLocation, customLatLng, mapAutoCentering])

  const clickSpecialOffer = useCallback((excursions: Excursion[]) => {
    const speacialOfferExc = excursions?.find((exc) => exc?.discount)
    console.log(speacialOfferExc, excursions)
    if (speacialOfferExc === undefined) return
    dispatch(
      setModalParams({
        excursionId: speacialOfferExc.id,
        oldPrice: speacialOfferExc?.old_price?.toString(),
        newPrice: speacialOfferExc?.price?.toString(),
      }),
    )
    dispatch(setModal({ name: 'special__offer' }))
  }, [])

  // map events
  const onLoad = useCallback(
    function callback(map: any) {
      dispatch(setMapLoaded(false))
      setMap(map)

      const bounds = new window.google.maps.LatLngBounds()
      markers.forEach((marker) => {
        bounds.extend(
          new google.maps.LatLng(marker.coordinate?.latitude, marker.coordinate?.longitude),
        )
      })

      // Map reset button
      const centerControlDiv = document.createElement('div')
      centerControlDiv.classList.add('centerControl')
      centerControlDiv.classList.add('hide')
      const centerControl = createCenterControl({ onClickHandler: resetLocation })
      centerControlDiv.appendChild(centerControl)

      map.controls[google.maps.ControlPosition.LEFT_TOP].push(centerControlDiv)
      setMap(map)
      dispatch(setMapLoaded(true))
    },
    [excursions],
  )

  useEffect(() => {
    if (map === undefined || map === null || excursions?.length === 0) return
    const hasDiscountExcursion = excursions.some((exc) => exc?.discount)
    if (!hasDiscountExcursion) return
    const specialOfferButton = createSpecialOfferControl({
      onClickHandler: () => clickSpecialOffer(excursions),
    })

    map.controls[google.maps.ControlPosition.TOP_CENTER].push(specialOfferButton)

    setMap(map)
    dispatch(setMapLoaded(true))
  }, [map, excursions])

  const onUnmount = useCallback(function callback(_map: any) {
    setMap(null)
  }, [])

  useEffect(() => {
    if (mapAutoCentering) {
      const centerBtn = document.querySelector('.centerControl')
      centerBtn?.classList.add('hide')
      return
    }
    const centerBtn = document.querySelector('.centerControl')
    centerBtn?.classList.remove('hide')
  }, [mapAutoCentering])

  const handleMarkerClick = useCallback(
    async (marker: MapPoint, index?: number) => {
      ReactGA.event({
        category: 'click',
        action: 'WA_MarkerClick',
        label: marker.title,
      })

      const audioURI = 'audioURI' in marker ? (marker as InterestPoint | TExcursionPoint).audioURI : undefined
      const audioFile = audioURI && audioURI.trim() !== '' ? audioURI : undefined
      setPointerDataset({
        xid: marker.id,
        name: marker.title,
        point: {
          lat: marker.coordinate?.latitude,
          lon: marker.coordinate?.longitude,
        },
        info: {
          descr: marker?.full_description ?? marker.description,
        },
        preview: {
          source: marker.gallery?.[0]?.uri,
        },
        gallery: marker?.gallery ?? [],
        audioFile,
      })
      // console.log(marker)
      setSelectedPoint(marker.id)
      // Update currentStep if in excursion mode and index is provided
      if (selectedExcursion && typeof index === 'number') {
        return setCurrentTrackStep(index)
      }
      // Trigger a re-render
      setUpdateTrigger((prev) => prev + 1)
    },
    [selectedExcursion, currentStep],
  )

  const onZoomChanged = useCallback(() => {
    const zoom = map?.getZoom()
    if (zoom !== undefined) {
      setCurrentZoom(zoom ?? 13)
    }
    //??? dispatch(setMapAutoCentering(false))
  }, [map])

  const onDragEnd = useCallback(() => {
    dispatch(setMapAutoCentering(false))

    if (!map) return
    const newCenter = map.getCenter()
    if (!newCenter) return

    setLastManualCenter({ lat: newCenter.lat(), lng: newCenter.lng() })
  }, [map])

  const fetchPosition = useCallback(
    debounce(async (newLocation: Coordinates) => {
      dispatch(getPointsThunk() as any)
    }, 300),
    [i18n.language],
  )

  const resetLocation = useCallback(() => {
    setCustomLatLng(null)
    setLastManualCenter(null)
    dispatch(setMapAutoCentering(true))
    try {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          let { latitude, longitude } = position.coords
          //TODO: Make it a nicer solution
          const isNotRomePosition =
            (latitude > 42.05 || latitude < 41.72) && (longitude < 12.146 || longitude > 12.7777)
          if (isNotRomePosition) {
            latitude = 41.89021
            longitude = 12.4923
          }
          dispatch(setUserLocation({ latitude, longitude }))
          dispatch(getPointsThunk() as any)
          dispatch(setLocationManual(false))
          map?.setCenter({ lat: latitude, lng: longitude })
        },
        (error) => {
          console.error('Error obtaining location:', error)
          //TODO: another hack
          dispatch(setUserLocation({ latitude: 41.89021, longitude: 12.4923 }))
        },
        {
          enableHighAccuracy: true,
          timeout: 1000,
          maximumAge: Infinity,
        },
      )
    } catch {
      console.error('Geo error')
    }
  }, [locale])

  const renderCount = useRef<number>(0)

  useEffect(() => {
    dispatch(setLocale(i18n.language as Language))

    if (renderCount.current >= 1) {
      resetLocation()
    }

    renderCount.current = renderCount.current + 1
  }, [i18n.language])

  useEffect(() => {
    if (customLatLng) {
      fetchPosition({
        latitude: customLatLng.latitude,
        longitude: customLatLng.longitude,
      })
    }
  }, [customLatLng])

  useEffect(() => {
    if (locationManual && map && userLocation) {
      map.setCenter({ lat: userLocation.latitude, lng: userLocation.latitude })
    }
  }, [locationManual])

  const getFilteredExcursionPoints = (points: TExcursionPoint[]): TExcursionPoint[] =>
    points.filter((item) => item.title)

  const memoizedMarkerList = useMemo(() => {
    //if (!window.google) return [] //TODO: this could result in racing condition (map not loaded yet, but markers are - fix this)
    const r = (
      selectedExcursion
        ? [...getFilteredExcursionPoints(selectedExcursion.landmarks)]
        : [...markers]
    ).map((marker, idx) => {
      const isCurrentStep = selectedExcursion && idx === currentStep
      const isSelected = markDto?.xid === marker.id
      return {
        key: idx,
        icon: createMarkerIcon({
          id: marker?.id,
          window: window,
          idx: selectedExcursion ? idx : null,
          rank: marker.rank,
          category: marker.type,
          zoom: map?.getZoom(),
          heading: null,
          isHighlighted: isCurrentStep || selectedExcursion?.landmarks.length === 1 || isSelected,
          selectedPoint: selectedPoint,
          iconUsing: 'main',
        }),
        category: marker.type,
        clickable: true,
        onClick: () => handleMarkerClick(marker, selectedExcursion ? idx : undefined),
        position: {
          lat: marker.coordinate?.latitude,
          lng: marker.coordinate?.longitude,
        },
        isOpen: false,
        zIndex: isCurrentStep ? 2000 : isSelected ? 1000 : 0, // Prioritize current step, then selected marker
      }
    })
    return r
  }, [markers, currentZoom, selectedExcursion, currentStep, updateTrigger])
  useEffect(() => {
    if (isExcursionBeingSelected) {
      dispatch(getExcursionCategories() as any)
    }

    if (selectedExcursion && currentStep < selectedExcursion?.landmarks.length) {
      void handleMarkerClick(selectedExcursion.landmarks.filter((item) => item.title)[currentStep])
    }
  }, [isExcursionBeingSelected])

  useLayoutEffect(() => {
    dispatch(getExcursionPoints() as any)
  }, [])

  const startExcursion = (id: string, firstPointId: string) => {
    setExcursionBeingSelected(false)
    setPointerDataset(null)
    const current: Excursion | undefined = excursions.find((item) => item.id === id)

    if (current) {
      const firstPoint = current.landmarks.find((item) => item.id === firstPointId)
      if (firstPoint) {
        setPlaces([
          { ...firstPoint },
          ...current.landmarks.filter((item) => item.id !== firstPointId),
        ])
        setSelectedExcursion({
          ...current,
          landmarks: [firstPoint, ...current.landmarks.filter((item) => item.id !== firstPointId)],
        })
        setCurrentStep(0)

        ReactGA.event({
          category: 'excursion',
          action: 'WA_ExcursionStart',
          label: `${id}`,
        })
      } else {
        console.error('First point not found in the tours!')
      }
    } else {
      console.error('Excursion not found!')
    }
  }

  const closeExcursion = () => {
    setExcursionBeingSelected(true)
    setPlaces([])
    setCurrentStep(0)
    setSelectedExcursion(null)
  }

  useEffect(() => {
    if (selectedExcursion && currentStep < selectedExcursion?.landmarks.length) {
      void handleMarkerClick(selectedExcursion.landmarks.filter((item) => item.title)[currentStep])
    }
  }, [currentStep, selectedExcursion])

  useEffect(() => {
    if (isExcursionBeingSelected && selectedExcursion) {
      void handleMarkerClick(selectedExcursion.landmarks.filter((item) => item.title)[currentStep])
      setExcursionBeingSelected(false)
    }
  }, [isExcursionBeingSelected])

  function onCloseInfoWindow() {
    setPointerDataset(null)
    setUpdateTrigger((prev) => prev + 1)
  }

  function onCloseInfoWindowRoute() {
    closeExcursion()
    setPointerDataset(null)
    setExcursionBeingSelected(true)
  }

  const selectedExcursionLandmarksCount = selectedExcursion?.landmarks.length

  return (
    <div>
      {/*TODO check audio div  audioPlaying != null ? createAudioDiv() : null*/}
      {/* <FirstTimePopup /> */}
      <div className="map" style={{ position: 'relative' }}>
        <SentryErrorBoundary boundaryId="googlemap">
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={mapCenter}
            zoom={currentZoom}
            onLoad={onLoad}
            onUnmount={onUnmount}
            onZoomChanged={onZoomChanged}
            onDragEnd={onDragEnd}
            options={mapOptions as any}
          >
            {selectedExcursion && places.length > 0 && (
              <MapDirectionsRenderer places={places} travelMode={'WALKING'} />
            )}
            <TransitLayer />
            {
              // eslint-disable-next-line no-constant-condition
              selectedExcursion ? (
                // Render markers without clustering when an excursion is selected
                memoizedMarkerList.map((marker) => (
                  <Marker
                    key={marker.key}
                    position={marker.position}
                    icon={marker.icon}
                    onClick={marker.onClick}
                    zIndex={marker.zIndex}
                  />
                ))
              ) : (
                // Use MarkerClusterer when no excursion is selected
                <div>
                  <ClusteredMarkers key={memoizedMarkerList.length} markers={memoizedMarkerList} />
                </div>
              )
            }
            <Marker
              key={80000000}
              options={circleOptions}
              icon={createMarkerIcon({
                id: null,
                window: window,
                idx: null,
                rank: 1,
                category: 'user-current-location',
                zoom: map?.getZoom(),
                heading: userPosition.heading,
                isHighlighted: false,
                selectedPoint: selectedPoint,
                iconUsing: 'main',
              })}
              position={{ lat: userPosition.latitude, lng: userPosition.longitude }}
            />
          </GoogleMap>
        </SentryErrorBoundary>
        <SentryErrorBoundary boundaryId="infowindow">
          <InfoWindow
            active={!!markDto && !isExcursionBeingSelected}
            data={markDto}
            onClose={onCloseInfoWindow}
            excursionStart={!!selectedExcursion}
            currentStep={currentStep}
            onCloseRoute={onCloseInfoWindowRoute}
            setCurrentStep={setCurrentTrackStep}
            pointsLength={selectedExcursionLandmarksCount}
          />
        </SentryErrorBoundary>
        <SentryErrorBoundary boundaryId="routewindow">
          <RoutesWindow
            markers={[]} //{markers}
            isExcursionBeingSelected={isExcursionBeingSelected}
            setExcursionBeingSelected={setExcursionBeingSelected}
            data={selectedExcursion}
            active={isExcursionBeingSelected && !selectedExcursion}
            onClose={() => setExcursionBeingSelected(false)}
            startExcursion={startExcursion}
            closeExcursion={closeExcursion}
            initialExcursionId={initialExcursionId}
          />
        </SentryErrorBoundary>
      </div>
    </div>
  )
}
export default MainMap
